import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type EventType = 'view' | 'click' | 'save' | 'unsave' | 'ai_sort_requested' | 'ai_sort_succeeded' | 'ai_sort_failed' | 'ai_sort_fallback' | 'ai_recommend_succeeded' | 'ai_recommend_failed' | 'ai_enrich_succeeded' | 'ai_enrich_failed' | 'ai_validate_succeeded' | 'ai_validate_failed';

export const useAnalytics = () => {
  const { user } = useAuth();

  const trackEvent = useCallback(async (
    eventType: EventType,
    metadata?: Record<string, any>,
    botId?: string,
    sessionId?: string
  ) => {
    try {
      // For AI events, log to console with structured data
      if (eventType.startsWith('ai_')) {
        console.log(`Analytics: ${eventType}`, {
          timestamp: new Date().toISOString(),
          user_id: user?.id || 'anonymous',
          session_id: sessionId || null,
          ...metadata
        });
        return;
      }

      // For bot analytics, use the existing table structure
      if (botId) {
        await supabase.from('bot_analytics').insert({
          bot_id: botId,
          user_id: user?.id || null,
          session_id: sessionId || null,
          event_type: eventType,
          user_agent: navigator.userAgent,
          // IP address will be handled by the server
        });
      }
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  }, [user]);

  const trackSearch = useCallback(async (
    query: string, 
    resultsCount: number,
    sessionId?: string
  ) => {
    try {
      await supabase.from('search_queries').insert({
        query: query.trim(),
        user_id: user?.id || null,
        session_id: sessionId || null,
        results_count: resultsCount
      });
    } catch (error) {
      console.error('Search tracking error:', error);
    }
  }, [user]);

  return {
    trackEvent,
    trackSearch
  };
};